import { useEffect, useState, useCallback, memo } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Skeleton,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import {
    ArrowRightUp,
    AltArrowRight,
    PenNewSquare,
} from "@solar-icons/react/ssr";
import { useSelector, useDispatch } from "react-redux";

import { request } from "@/Api";
import { selectPersonId } from "@/auth/authSlice";
import { setError } from "@/nav/navSlice";

// ─── Shortcut Dialog ────────────────────────────────────────────────────────────

const ShortcutDialog = ({ shortcut, onClose, onSave, onDelete }) => {
    const [name, setName] = useState(shortcut.name || "");
    const [url, setUrl] = useState(shortcut.url || "");
    const isCreate = !shortcut.id;

    useEffect(() => {
        setName(shortcut.name || "");
        setUrl(shortcut.url || "");
    }, [shortcut]);

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {isCreate ? "Add Shortcut" : "Edit Shortcut"}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    label="Name"
                    fullWidth
                    size="small"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mt: 1, mb: 2 }}
                />
                <TextField
                    label="URL"
                    fullWidth
                    size="small"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://..."
                />
            </DialogContent>
            <DialogActions>
                {!isCreate && (
                    <Button
                        color="error"
                        onClick={() => onDelete(shortcut.id)}
                        sx={{ mr: "auto" }}
                    >
                        Delete
                    </Button>
                )}
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!name.trim() || !url.trim()}
                    onClick={() =>
                        onSave({
                            ...shortcut,
                            name: name.trim(),
                            url: url.trim(),
                        })
                    }
                >
                    {isCreate ? "Add" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Personal Shortcuts Widget ──────────────────────────────────────────────────

const PersonalShortcuts = memo(({ gridCols = 1 }) => {
    const theme = useTheme();
    const [shortcuts, setShortcuts] = useState(null);
    const [editingShortcut, setEditingShortcut] = useState(null);
    const personId = useSelector(selectPersonId);
    const dispatch = useDispatch();

    // Responsive columns: 1/3 width → 1col, 2/3 → 2col, 3/3 → 3col
    const columns = Math.max(1, Math.min(3, gridCols));

    const fetchShortcuts = useCallback(() => {
        request
            .get("/shortcuts/personal")
            .then((response) => {
                setShortcuts(response.data);
            })
            .catch((err) => {
                setShortcuts([]);
                dispatch(
                    setError({
                        message:
                            err?.response?.data?.detail ||
                            "Failed to load shortcuts",
                    }),
                );
            });
    }, []);

    useEffect(() => {
        fetchShortcuts();
    }, [fetchShortcuts]);

    const handleSave = useCallback(
        (shortcut) => {
            const url =
                shortcut.url && !shortcut.url.startsWith("http")
                    ? `https://${shortcut.url}`
                    : shortcut.url;
            const payload = { ...shortcut, url };

            if (shortcut.id) {
                request
                    .put(`/shortcuts/${shortcut.id}`, payload)
                    .then((response) => {
                        if (response.status === 200) {
                            setShortcuts((prev) =>
                                prev.map((s) =>
                                    s.id === response.data.id
                                        ? response.data
                                        : s,
                                ),
                            );
                            setEditingShortcut(null);
                        }
                    })
                    .catch((err) => {
                        dispatch(
                            setError({
                                message:
                                    err?.response?.data?.detail ||
                                    "Failed to update shortcut",
                            }),
                        );
                    });
            } else {
                request
                    .post("/shortcuts", { ...payload, person_id: personId })
                    .then((response) => {
                        if (response.status === 200) {
                            setShortcuts((prev) => [...prev, response.data]);
                            setEditingShortcut(null);
                        }
                    })
                    .catch((err) => {
                        dispatch(
                            setError({
                                message:
                                    err?.response?.data?.detail ||
                                    "Failed to create shortcut",
                            }),
                        );
                    });
            }
        },
        [personId],
    );

    const handleDelete = useCallback(
        (shortcutId) => {
            request
                .delete(`/shortcuts/${shortcutId}`)
                .then((response) => {
                    if (response.status === 200) {
                        setShortcuts((prev) =>
                            prev.filter((s) => s.id !== shortcutId),
                        );
                        setEditingShortcut(null);
                    }
                })
                .catch((err) => {
                    dispatch(
                        setError({
                            message:
                                err?.response?.data?.detail ||
                                "Failed to delete shortcut",
                        }),
                    );
                });
        },
        [dispatch],
    );

    const containerSx = {
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        height: "100%",
    };

    if (!shortcuts) {
        return (
            <Box sx={containerSx}>
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter Tight, sans-serif",
                        color: "text.primary",
                        lineHeight: 1,
                    }}
                >
                    Shortcuts
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: `repeat(${columns}, 1fr)`,
                        },
                        gap: "20px",
                    }}
                >
                    {Array.from({ length: columns }, (_, i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={94}
                            sx={{ borderRadius: "8px" }}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={containerSx}>
            {/* Header */}
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "Inter Tight, sans-serif",
                    color: "text.primary",
                    lineHeight: 1,
                }}
            >
                Shortcuts
            </Typography>

            {/* Shortcut grid */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: `repeat(${columns}, 1fr)`,
                    },
                    gap: "20px",
                }}
            >
                {shortcuts.map((shortcut) => (
                    <Box
                        key={shortcut.id}
                        component="a"
                        href={shortcut.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            background: theme.palette.action.hover,
                            borderRadius: "8px",
                            height: 94,
                            position: "relative",
                            overflow: "hidden",
                            cursor: "pointer",
                            textDecoration: "none",
                            display: "block",
                            "&:hover": {
                                background: theme.palette.action.selected,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                left: 20,
                                top: 20,
                                opacity: 0.5,
                                display: "flex",
                            }}
                        >
                            <ArrowRightUp
                                size={16}
                                color={theme.palette.text.primary}
                                weight="Outline"
                            />
                        </Box>
                        <Typography
                            sx={{
                                position: "absolute",
                                left: 42,
                                top: 21,
                                fontSize: 12,
                                color: "text.secondary",
                                fontFamily: "Inter Tight, sans-serif",
                                lineHeight: 1,
                            }}
                        >
                            Link
                        </Typography>
                        <Box
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingShortcut(shortcut);
                            }}
                            sx={{
                                position: "absolute",
                                right: 12,
                                top: 12,
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                opacity: 0.5,
                                transition: "opacity 0.15s, background 0.15s",
                                "&:hover": {
                                    opacity: 1,
                                    background: theme.palette.action.selected,
                                },
                            }}
                        >
                            <PenNewSquare
                                size={14}
                                color={theme.palette.text.secondary}
                                weight="Outline"
                            />
                        </Box>
                        <Typography
                            noWrap
                            sx={{
                                position: "absolute",
                                left: 20,
                                right: 20,
                                top: 57,
                                fontSize: 14,
                                color: "text.primary",
                                fontFamily: "Inter Tight, sans-serif",
                                lineHeight: 1.3,
                            }}
                        >
                            {shortcut.name}
                        </Typography>
                    </Box>
                ))}
                {/* Always-visible add button */}
                <Box
                    component="button"
                    onClick={() => setEditingShortcut({ name: "", url: "" })}
                    sx={{
                        background: "transparent",
                        border: `1px dashed ${theme.palette.secondary.main}`,
                        borderRadius: "8px",
                        height: 94,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                        fontSize: 24,
                        "&:hover": {
                            borderColor: "text.secondary",
                            color: "text.primary",
                        },
                    }}
                >
                    +
                </Box>
            </Box>

            {editingShortcut !== null && (
                <ShortcutDialog
                    shortcut={editingShortcut}
                    onClose={() => setEditingShortcut(null)}
                    onSave={handleSave}
                    onDelete={handleDelete}
                />
            )}
        </Box>
    );
});

PersonalShortcuts.displayName = "PersonalShortcuts";

export default PersonalShortcuts;
